import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../ui/InventorySlideOver';
import {
  BorkConnectionError,
  saveBorkConnection,
  type BorkPosConnection,
} from '../../services/restaurant/restaurantBorkConnectionService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  /** The existing connection when editing; null opens the form for a first connection. */
  connection: BorkPosConnection | null;
  onSaved: () => void;
}

/**
 * Connect (or re-connect) a restaurant's own Bork POS gateway.
 *
 * The server checks the credentials against the live gateway before saving, so this form's failure
 * states are real answers from the POS rather than guesses: a rejected save means nothing was
 * written, and the `reason` it comes back with decides which fix we suggest.
 */
const SaveBorkConnectionModal: React.FC<Props> = ({ isOpen, onClose, restaurantId, connection, onSaved }) => {
  const { t } = useTranslation();
  const [baseUrl, setBaseUrl] = useState('');
  const [appId, setAppId] = useState('');
  const [centerName, setCenterName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!connection;

  // Re-seed on every open so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!isOpen) return;
    setBaseUrl(connection?.baseUrl ?? '');
    setAppId('');
    setCenterName(connection?.centerName ?? '');
    setError(null);
  }, [isOpen, connection]);

  const canSave = !!baseUrl.trim() && (isEdit || !!appId.trim());

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await saveBorkConnection(restaurantId, {
        baseUrl: baseUrl.trim(),
        ...(appId.trim() ? { appId: appId.trim() } : {}),
        centerName: centerName.trim() || null,
      });
      onSaved();
    } catch (err) {
      // `reason` rides only on the 400 from a failed live credential check. Everything else (403,
      // 404, 500, network) has no reason, and its server text is untranslated English written for
      // operators — so those get one translated generic line instead, with the detail logged.
      const reason = err instanceof BorkConnectionError ? err.reason : undefined;
      if (!reason) console.error('Failed to save Bork connection:', err);
      setError(t(`restaurantBorkConnection.error.${reason ?? 'generic'}`));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:border-zinc-400 dark:focus:border-zinc-500 transition';
  const labelClass = 'block mb-2 text-sm font-medium text-zinc-700 dark:text-zinc-300';
  const helpClass = 'mt-1.5 text-xs text-zinc-500 dark:text-zinc-400';

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('restaurantBorkConnection.editTitle') : t('restaurantBorkConnection.connectTitle')}
      description={t('restaurantBorkConnection.formDescription')}
      closeLabel={t('restaurantBorkConnection.close')}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={slideOverCancelClass}>
            {t('restaurantBorkConnection.cancel')}
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !canSave} className={slideOverPrimaryClass}>
            {saving ? t('restaurantBorkConnection.checking') : t('restaurantBorkConnection.save')}
          </button>
        </>
      }
    >
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="space-y-5">
        <div>
          <label className={labelClass} htmlFor="bork-base-url">
            {t('restaurantBorkConnection.baseUrlLabel')}
          </label>
          <input
            id="bork-base-url"
            type="text"
            value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)}
            placeholder="https://xxxxxxxx.trivecgateway.com"
            className={inputClass}
          />
          <p className={helpClass}>{t('restaurantBorkConnection.baseUrlHelp')}</p>
        </div>

        <div>
          <label className={labelClass} htmlFor="bork-app-id">
            {t('restaurantBorkConnection.appIdLabel')}
          </label>
          <input
            id="bork-app-id"
            type="password"
            autoComplete="new-password"
            value={appId}
            onChange={e => setAppId(e.target.value)}
            placeholder={isEdit ? t('restaurantBorkConnection.appIdKeepPlaceholder') : '••••••••'}
            className={inputClass}
          />
          <p className={helpClass}>
            {isEdit ? t('restaurantBorkConnection.appIdEditHelp') : t('restaurantBorkConnection.appIdHelp')}
          </p>
        </div>

        <div>
          <label className={labelClass} htmlFor="bork-center-name">
            {t('restaurantBorkConnection.centerNameLabel')}
          </label>
          <input
            id="bork-center-name"
            type="text"
            value={centerName}
            onChange={e => setCenterName(e.target.value)}
            placeholder={t('restaurantBorkConnection.centerNamePlaceholder')}
            className={inputClass}
          />
          <p className={helpClass}>{t('restaurantBorkConnection.centerNameHelp')}</p>
        </div>
      </div>
    </InventorySlideOver>
  );
};

export default SaveBorkConnectionModal;
